import {
  DataGridContainer,
  SelectableToolbar,
  PresentedRows,
} from '@gogame/data-grid';
import {
  Paper,
  Divider,
  View,
  Grid,
  CardContent,
  Typography,
  Link,
  Stack,
  LegacyPagination,
  Toolbar,
  Actions,
  Action,
  NoResults,
} from '@gogame/components';

import React from 'react';
import { useScreenState, useScreenDispatch } from '@gogame/screens';
import { useChildComponent } from '@gogame/hooks-ts';
import { useLoadCreativesTracker, useLoadDimensions } from '../../data';
import ConceptDetails from 'scenes/Gallery/GalleryPage/Concepts/ConceptDetails';
import CreativeDetails from 'scenes/Gallery/GalleryPage/Creatives/CreativeDetails';
import CreativesTrackerToolbar from '../CreativesTrackerToolbar';
import MetricList, { Metric } from '../MetricList';
import Attributes, { Attribute } from '../Attributes';
import Tile from './Tile';
import PreviewWithScrubber from 'scenes/Gallery/components/File/PreviewWithScrubber/PreviewWithScrubber';
import { SET_TABLE_SETTINGS, SET_SELECTED_IDS } from '../../reducers/constants';
import { DIMENSION_CREATIVE, DIMENSION_CONCEPT } from '../../constants';

const creativeTypes = {
  3: 'video',
  1: 'banner',
};

function getParams({
  filtersCollection,
  tableSettings,
  metrics,
  selectedIds,
  searchQuery,
  dimension,
  advancedFilters,
}) {
  return {
    filtersCollection,
    tableSettings,
    metrics,
    selectedIds,
    searchQuery,
    dimension,
    advancedFilters,
  };
}

function areSelectionsEqual(a, b = []) {
  const [aKey, bKey] = [a, b].map((arr) => [...arr].sort().join('$$$'));
  return aKey === bKey;
}

function getDetails(dimension) {
  switch (dimension) {
    case DIMENSION_CONCEPT:
      return ConceptDetails;
    case DIMENSION_CREATIVE:
      return CreativeDetails;
    default:
      return () => null;
  }
}

const CreativesTrackerTiles = () => {
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const [childComponent, openChildComponent] = useChildComponent();

  const params = getParams(screenState);

  const { data, isLoading, isRefetching } = useLoadCreativesTracker(
    params,
    screenState.ready
  );

  const { data: dimensions } = useLoadDimensions();

  const open = (id) => {
    openChildComponent(getDetails(screenState.dimension), { id });
  };

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const [selected, setSelected] = React.useState(() => []);
  const handlePresentSelected = () => {
    dispatch({
      type: SET_SELECTED_IDS,
      payload: selected,
    });
    setSelected([]);
  };

  const handleClearPresentedRows = () => {
    dispatch({
      type: SET_SELECTED_IDS,
      payload: undefined,
    });
  };

  return (
    <DataGridContainer component={Paper}>
      {childComponent}
      <Toolbar disableGutters sx={{ position: 'relative' }}>
        <CreativesTrackerToolbar dimensions={dimensions} />
        {selected?.length ? (
          <SelectableToolbar
            label={`${selected.length} selected`}
            onUnselectAll={() => setSelected([])}
          >
            <Actions>
              <Action
                label={'Present selected'}
                button
                disabled={areSelectionsEqual(selected, screenState.selectedIds)}
                onClick={handlePresentSelected}
              />
            </Actions>
          </SelectableToolbar>
        ) : null}
      </Toolbar>
      <Divider />
      <DataGridContainer>
        <View
          loading={isLoading}
          fetching={isRefetching}
          fetchingOffset
          sticky={true}
        >
          <div
            style={{
              overflow: 'auto',
              padding: '16px',
              display: 'flex',
              height: '100%',
            }}
          >
            {data?.rows?.length ? (
              <Grid container spacing={2}>
                {data?.rows?.map((item) => {
                  const dimension = item?.Dimension;
                  const {
                    applications,
                    creativeType,
                    name,
                    thumbnailUrl,
                    videoLength,
                    id,
                    scrubbingUrls,
                  } = dimension;

                  return (
                    <Grid item lg={3} md={4} sm={6} xs={12}>
                      <Tile
                        selectable={true}
                        selected={selected.includes(id)}
                        onSelect={(e) => {
                          setSelected((s) => {
                            if (e.target.checked) {
                              return [...s, id];
                            } else {
                              return s.filter((el) => el !== id);
                            }
                          });
                        }}
                      >
                        <PreviewWithScrubber
                          src={thumbnailUrl}
                          aspectRatio={'36/19'}
                          width={'100%'}
                          height={'auto'}
                          scrubbingUrls={scrubbingUrls}
                          attributes={
                            <Stack
                              direction={'row'}
                              sx={{
                                justifyContent: 'space-between',
                              }}
                            >
                              <Attributes>
                                {applications?.map((app) => (
                                  <Attribute type={'app'} label={app.name} />
                                ))}
                                {creativeType && (
                                  <Attribute
                                    type={creativeTypes[creativeType.id]}
                                    label={creativeType.name}
                                  />
                                )}
                              </Attributes>
                              {videoLength ? (
                                <Attributes>
                                  <Attribute
                                    type={'time'}
                                    value={videoLength}
                                  />
                                </Attributes>
                              ) : null}
                            </Stack>
                          }
                        />
                        <CardContent>
                          <Typography noWrap tooltip={name} gutterBottom>
                            <Link onClick={() => open(id)}>{name}</Link>
                          </Typography>
                          <MetricList data={item}>
                            {data.cols.map((col) => (
                              <Metric
                                field={col.metricId}
                                type={col.type}
                                key={col.metricId}
                                label={col.displayName}
                              />
                            ))}
                          </MetricList>
                        </CardContent>
                      </Tile>
                    </Grid>
                  );
                })}
              </Grid>
            ) : isLoading ? null : (
              <NoResults
                inited={screenState.ready}
                notInitedLabel={
                  <>
                    <div>
                      Please choose one of your Presets or select metrics,
                      filters and graph settings, in order to load data.
                    </div>
                    <div>
                      Please note that data will load, only once you click
                      “Apply”.
                    </div>
                  </>
                }
              />
            )}
          </div>
        </View>
      </DataGridContainer>
      <Divider />
      <Toolbar disableGutters>
        <Stack
          direction={'row'}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <PresentedRows
            presentedRows={screenState.selectedIds}
            onClearPresentedRows={handleClearPresentedRows}
          />
          <LegacyPagination
            limit={screenState.tableSettings.limit}
            offset={screenState.tableSettings.offset}
            count={data?.count || 0}
            {...data?.paging}
            onChange={handleChangeSettings}
          />
        </Stack>
      </Toolbar>
    </DataGridContainer>
  );
};

export default CreativesTrackerTiles;
